import React, { createContext, useContext, useState } from 'react';
import IframeModal from './IframeModal';
import PhotoGalleryModal from './PhotoGalleryModal';
import VideoModal from './VideoModal';

// Create context for global modals
const GlobalModalsContext = createContext();

export const useGlobalModals = () => {
  const context = useContext(GlobalModalsContext);
  if (!context) {
    throw new Error('useGlobalModals must be used within a GlobalModalsProvider');
  }
  return context;
};

// Global Modals Provider
export const GlobalModalsProvider = ({ children }) => {
  // Iframe Modal State
  const [iframeModal, setIframeModal] = useState({
    isOpen: false,
    url: '',
    title: '',
    language: 'ar'
  });

  // Photo Gallery Modal State
  const [photoGalleryModal, setPhotoGalleryModal] = useState({
    isOpen: false,
    images: [],
    projectName: '',
    language: 'ar'
  });

  // Video Modal State
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    videoUrl: '',
    title: '',
    language: 'ar'
  });

  // Iframe Modal Functions
  const openIframeModal = (url, title, language = 'ar') => {
    setIframeModal({
      isOpen: true,
      url,
      title,
      language
    });
  };

  const closeIframeModal = () => {
    setIframeModal({
      isOpen: false,
      url: '',
      title: '',
      language: 'ar'
    });
  };

  // Photo Gallery Modal Functions
  const openPhotoGalleryModal = (images, projectName, language = 'ar') => {
    setPhotoGalleryModal({
      isOpen: true,
      images,
      projectName,
      language
    });
  };

  const closePhotoGalleryModal = () => {
    setPhotoGalleryModal({
      isOpen: false,
      images: [],
      projectName: '',
      language: 'ar'
    });
  };

  const value = {
    // Iframe Modal
    openIframeModal,
    closeIframeModal,
    iframeModal,

    // Photo Gallery Modal
    openPhotoGalleryModal,
    closePhotoGalleryModal,
    photoGalleryModal,

    // Video Modal
    openVideoModal: (videoUrl, title, language = 'ar') => {
      setVideoModal({ isOpen: true, videoUrl, title, language });
    },
    closeVideoModal: () => {
      setVideoModal({ isOpen: false, videoUrl: '', title: '', language: 'ar' });
    },
    videoModal,
  };

  return (
    <GlobalModalsContext.Provider value={value}>
      {children}
      
      {/* Global Modals */}
      <IframeModal
        isOpen={iframeModal.isOpen}
        onClose={closeIframeModal}
        url={iframeModal.url}
        title={iframeModal.title}
        language={iframeModal.language}
      />
      
      <PhotoGalleryModal
        isOpen={photoGalleryModal.isOpen}
        onClose={closePhotoGalleryModal}
        images={photoGalleryModal.images}
        projectName={photoGalleryModal.projectName}
        language={photoGalleryModal.language}
      />

      <VideoModal
        isOpen={videoModal.isOpen}
        onClose={() => setVideoModal({ isOpen: false, videoUrl: '', title: '', language: 'ar' })}
        videoUrl={videoModal.videoUrl}
        title={videoModal.title}
        language={videoModal.language}
      />
    </GlobalModalsContext.Provider>
  );
};

export default GlobalModalsProvider;
